import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { groupsAPI } from '../services/api';

const CampaignForm = ({ isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { groupId, campaignId } = useParams();
  
  useEffect(() => {
    // If editing, fetch campaign data
    if (isEdit && groupId && campaignId) {
      const fetchCampaign = async () => {
        try {
          setLoading(true);
          const response = await groupsAPI.getCampaignById(groupId, campaignId);
          const campaign = response.data.data;
          
          // Format dates for input fields (YYYY-MM-DD)
          const formatDate = (dateString) => {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          };
          
          setFormData({
            name: campaign.name || '',
            startDate: formatDate(campaign.startDate) || '',
            endDate: formatDate(campaign.endDate) || '',
            description: campaign.description || '',
            isActive: campaign.isActive !== undefined ? campaign.isActive : true
          });
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching campaign:', err);
          setError('Failed to load campaign data');
          setLoading(false);
        }
      };
      
      fetchCampaign();
    }
  }, [isEdit, groupId, campaignId]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // Validate form
      if (!formData.name) {
        setError('Campaign name is required');
        setLoading(false);
        return;
      }
      
      if (!formData.startDate || !formData.endDate) {
        setError('Start date and end date are required');
        setLoading(false);
        return;
      }
      
      // Validate dates
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate < startDate) {
        setError('End date cannot be before start date');
        setLoading(false);
        return;
      }
      
      let response;
      
      if (isEdit && groupId && campaignId) {
        // Update existing campaign
        response = await groupsAPI.updateCampaign(groupId, campaignId, formData);
        setSuccess('Campaign updated successfully');
      } else if (groupId) {
        // Create new campaign
        response = await groupsAPI.createCampaign(groupId, formData);
        setSuccess('Campaign created successfully');
        
        // Reset form after successful creation
        setFormData({
          name: '',
          startDate: '',
          endDate: '',
          description: '',
          isActive: true
        });
      }
      
      // Navigate after a short delay to show success message
      setTimeout(() => {
        navigate(`/admin/groups/${groupId}`);
      }, 1500);
      
    } catch (err) {
      console.error('Campaign form error:', err);
      setError(
        err.response?.data?.message || 
        'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="mb-4">
      <Card.Header>
        <h4>{isEdit ? 'Edit Campaign' : 'Add New Campaign'}</h4>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Campaign Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter campaign name"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter campaign description"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="isActive"
              label="Active"
              checked={formData.isActive}
              onChange={handleChange}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-between">
            <Button 
              variant="secondary" 
              onClick={() => navigate(`/admin/groups/${groupId}`)}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Campaign' : 'Create Campaign')}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default CampaignForm;
